import { InMemoryCache, ApolloClient, from, gql } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import uuid from 'uuid/v4';

import { ERROR_CODES } from 'constants/errors';
import config from 'utils/config';
import auth from 'utils/auth';
import { getStoredLocale } from 'utils/persistedLocale';
import { bookingStatusTypes } from 'types/booking';

import { typeDefs, resolvers, defaultClientData } from './state';

const cache = new InMemoryCache({
  typePolicies: {
    BookingItem: {
      fields: {
        isPrrRefunded: {
          merge: (existing, incoming) => {
            if (incoming === true) return true;
            if (existing === true) return true;
            return incoming !== undefined ? incoming : existing;
          }
        },
        isPrrAttempted: {
          merge: (existing, incoming) => {
            if (incoming === true) return true;
            if (existing === true) return true;
            return incoming !== undefined ? incoming : existing;
          }
        },
        refundMethod: {
          merge: (existing, incoming) => {
            if (existing && !incoming) return existing;
            return incoming !== undefined ? incoming : existing;
          }
        },
        status: {
          read(_, { readField }) {
            const isPrrRefunded = readField('isPrrRefunded');
            const isPrrAttempted = readField('isPrrAttempted');
            const isCheckedIn = readField('isCheckedIn');
            const isSuspended = readField('isSuspended');
            const cancelled = readField('cancelled');
            const provisional = readField('provisional');
            const isPlaceholder = readField('isPlaceholder');
            const isUKEntryDenied = readField('isUKEntryDenied');

            const {
              CONFIRMED,
              PROVISIONAL,
              CANCELLED,
              TEMPORARILY_CANCELLED,
              CHECKED_IN,
              PRR_REFUNDED,
              PRR_ATTEMPTED,
              SUSPENDED,
              PLACEHOLDER,
              UKENTRYDENIED
            } = bookingStatusTypes;

            if (isPrrRefunded) {
              return PRR_REFUNDED;
            }

            if (isPrrAttempted) {
              return PRR_ATTEMPTED;
            }

            if (isCheckedIn) {
              return CHECKED_IN;
            }

            if (isSuspended) {
              return SUSPENDED;
            }

            if (isPlaceholder) {
              return PLACEHOLDER;
            }

            if (isUKEntryDenied && !cancelled && !provisional) {
              return UKENTRYDENIED;
            }

            if (!cancelled && !provisional) {
              return CONFIRMED;
            }

            if (!cancelled && provisional) {
              return PROVISIONAL;
            }

            if (cancelled && !provisional) {
              return CANCELLED;
            }

            return TEMPORARILY_CANCELLED;
          }
        }
      }
    }
  },
  possibleTypes: {
    Product: ['BookingItem'],
    BookingItem: ['Product', 'receiptProduct', 'PaymentsProduct'],
    Leg: ['LegFields'],
    LegFields: ['Leg'],
    receiptProduct: ['BookingItem'],
    PaymentsProduct: ['BookingItem']
  },
  dataIdFromObject: (object) => {
    // eslint-disable-next-line no-underscore-dangle
    switch (object.__typename) {
      case 'Booking':
        return object.reference;
      case 'User':
        return object.username;
      case 'Leg':
        // Legs are queried with different arguments (filters) across the app. For this reason, it is
        // not safe to cache by `id` as different legs objects with the same `id` might contain
        // different data.
        // It's instead safer to return `null` here so that the cache key falls back to being the full
        // path to the leg object in the query, e.g. `{bookingReference}.outbound({...filters...}).{index}`
        return null;
      case 'Payment':
        // The id returned for a payment by S3 can change after adding a new payment. For this reason, we can't
        // rely on the id for caching. The refId is unique so we will use that
        return object.refId;
      case 'BookingItem':
        return Number(object.ticketNumber) ? `BookingItem:${object.ticketNumber}` : undefined;
      case 'Passenger':
        return null;
      case 'Voucher':
        return object.id || object.reference || object.alias || undefined;
      default:
        return undefined;
    }
  }
});

export const batchHttpLink = new BatchHttpLink({
  uri: `${config('BFF_URL')}/graphql`,
  headers: {
    'x-api-key': config('API_GATEWAY_API_KEY'),
    'x-correlation-id': uuid()
  },
  credentials: 'include'
});

export const reportErrorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions, message, locations, path }) => {
      if (extensions && extensions.code === ERROR_CODES.S3_AUTHORISATION_ERROR) {
        auth.logout();
      }

      // eslint-disable-next-line no-console
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
  if (networkError) {
    // eslint-disable-next-line no-console
    console.error(`[Network error]: ${networkError}`);

    if (networkError.statusCode === 401) {
      const nextUrl = `${window.location.pathname}${window.location.search}`;
      auth.loginWithNextUrl(nextUrl);
    }
  }
});

export const addAuthorizationLink = setContext(async () => {
  let token;
  try {
    token = await auth.getAccessToken();
  } catch (error) {
    console.error(`[Authorization Error]: ${error.error_description || error.message}`);
  }

  if (token === null) {
    return null;
  }

  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
});

const client = new ApolloClient({
  link: from([reportErrorLink, addAuthorizationLink, batchHttpLink]),
  resolvers,
  typeDefs,
  cache,
  connectToDevTools: process.env.NODE_ENV !== 'production'
});

const initializeCache = () => {
  cache.writeQuery({
    query: gql`
      query InitializeCache {
        locale @client
        isLoggedIn @client
        searchParams @client {
          __typename
          outboundDate
          inboundDate
          origin
          destination
          returningOrigin
          returningDestination
          adults
          adults12To15
          adults16Plus
          children
          children4Only
          children5To11
          infants
          youths
          seniors
        }
        billingDetails @client {
          __typename
          firstName
          lastName
          addressLine1
          addressLine2
          addressLine3
          city
          postalCode
          countryCode
          email
          phoneCountryCode
          phoneNumber
        }
        passengerDetails @client
      }
    `,
    data: {
      locale: getStoredLocale() || defaultClientData.locale,
      isLoggedIn: defaultClientData.isLoggedIn,
      searchParams: defaultClientData.searchParams,
      billingDetails: defaultClientData.billingDetails,
      passengerDetails: defaultClientData.passengerDetails
    }
  });
};

initializeCache();

client.onResetStore(() => initializeCache());

export default client;
